<template>
  <div class="run-list">
    <BasePreloader v-if="loading && !runs.length" color="purple" size="l"/>
    <p v-else-if="!runs.length" class="run-list__empty">
      {{ emptyText || (showWho ? $t('carriers.import.history.EmptyAll') : $t('carriers.import.history.Empty')) }}
    </p>

    <div v-else>
      <!-- One row per read: one job, or the two a "Read both" started together (groupReads).
           A read that did not work is one line: why, and the one thing to do about it. A read
           that did shows its counts and two links, nothing to open. -->
      <div
        v-for="read in reads"
        :key="read.key"
        class="run-list__run"
        :class="{ 'run-list__run--current': read.runs.some(isCurrent) }"
      >
        <div class="run-list__head">
          <span class="run-list__badge" :class="`run-list__badge--${readTone(read)}`">{{ readTone(read) === 'success' ? '✓' : '!' }}</span>

          <div class="run-list__main">
            <p class="run-list__when">
              {{ formatWhen(read.lead.finishedAt || read.lead.updatedAt, $i18n.locale) }}
              <span class="run-list__kind">· {{ kindLabel(read) }}</span>
              <span v-if="isDailyRun(read.lead)" class="run-list__daily">{{ $t('carriers.import.daily.Badge') }}</span>
              <template v-if="read.runs.length === 1 && !failureOf(read)">
                <span v-if="isStopping(read.lead)" class="run-list__live">
                  · {{ $t('carriers.import.state.stopping') }}
                </span>
                <span v-else-if="stoppedEarly(read.lead)" class="run-list__partial">
                  · {{ $t('carriers.import.history.StoppedEarly') }}
                </span>
              </template>
            </p>

            <p v-if="showWho" class="run-list__who">
              <CarrierLogo :src="logoFor ? logoFor(read.lead) : null" :size="20"/>
              <span class="run-list__carrier">{{ read.lead.carrierName || $t('carriers.import.TheCarrier') }}</span>
              <span v-if="read.lead.scrapeAccountLabel" class="run-list__login">· {{ read.lead.scrapeAccountLabel }}</span>
            </p>

            <!-- Failed: the reason in one sentence, whichever half it came from, and one button.
                 Not the worker's own error text: it is a technical line nobody here can act on. -->
            <template v-if="failureOf(read)">
              <div class="run-list__failure">
                <p class="run-list__failure-text">
                  <!-- Which half, when the other one worked and is shown below. -->
                  <b v-if="okRuns(read).length">{{ $t(`carriers.import.kind.${kindOf(failureOf(read).runs[0])}`) }} ·</b>
                  {{ failureLine(read) }}
                </p>
                <BaseButton
                  v-if="failureAction(read)"
                  is-small
                  type="outline"
                  :loading="busy === read.key"
                  @click="act(read)"
                >{{ $t(failureAction(read).label) }}</BaseButton>
              </div>
              <TwoFaCodeCard
                v-if="enteringCode === read.key && codeAccountOf(read)"
                class="run-list__code"
                :account="codeAccountOf(read)"
                :run="failureOf(read).runs[0]"
                :carrier-name="read.lead.carrierName || $t('carriers.import.TheCarrier')"
              />
            </template>

            <!-- The halves that worked, with their counts and links, also beside a failed half:
                 payments that finished stay countable and downloadable when the policies crawl
                 did not. -->
            <template v-if="okRuns(read).length">
              <div
                v-for="run in okRuns(read)"
                :key="run.scrapeJobId"
                class="run-list__half"
                :class="{ 'run-list__half--paired': read.runs.length > 1 }"
              >
                <!-- Which half this is, and how it ended, when the read has two. -->
                <p v-if="read.runs.length > 1" class="run-list__half-kicker">
                  {{ $t(`carriers.import.kind.${kindOf(run)}`) }}
                  <span v-if="isStopping(run)" class="run-list__live">· {{ $t('carriers.import.state.stopping') }}</span>
                  <span v-else-if="stoppedEarly(run)" class="run-list__partial">· {{ $t('carriers.import.history.StoppedEarly') }}</span>
                </p>

                <!-- A live run counts profiles, not policies: the counts below stay at
                     zero until the import starts writing, which is most of a run. -->
                <p v-if="isJobActive(run)" class="run-list__sum">
                  <template v-if="progressOf(run)">
                    <b>{{ progressFigure(run) }}</b>
                    {{ $t(progressTotalKey(run), { total: formatCount(progressOf(run).total) }) }}
                    <span v-if="etaOf(run)" class="run-list__muted">· {{ etaOf(run) }}</span>
                  </template>
                  <template v-else>{{ unknownProgressText(run) }}</template>
                </p>

                <!-- The counts, only the ones that are not zero. A payments read from before its
                     counts were kept says only that it read. -->
                <p v-else-if="countsOf(run).length" class="run-list__sum">
                  <template v-for="(piece, i) in countsOf(run)">
                    <span :key="piece.key"><b>{{ formatCount(piece.count) }}</b> {{ $t(piece.label) }}</span
                    ><span v-if="i < countsOf(run).length - 1" :key="`${piece.key}-sep`"> · </span>
                  </template>
                </p>
                <p v-else-if="kindOf(run) === 'PAYMENTS' && !run.paymentCounts" class="run-list__sum">
                  {{ $t('carriers.import.summary.PaymentsRead', { carrier: run.carrierName || $t('carriers.import.TheCarrier') }) }}
                </p>

                <!-- A group's members are policies the run wrote too: counted above, and named here
                     so "41 imported" from one colectivo is not a mystery. -->
                <p v-if="!isJobActive(run) && memberPoliciesLine(run)" class="run-list__ml-sub">
                  {{ memberPoliciesLine(run) }}
                </p>
              </div>

              <!-- The policies behind the counts, and every payment as a file. -->
              <p v-if="settled(read) && (seeAllShown(read) || paymentsDone(read))" class="run-list__links">
                <button
                  v-if="seeAllShown(read)"
                  class="run-list__lnk"
                  type="button"
                  @click="seeAll(read)"
                >
                  {{ readPolicyCount(read) === null
                    ? $t('carriers.import.runRows.SeeAll')
                    : $t('carriers.import.runRows.SeeAllCount', { count: formatCount(readPolicyCount(read)) }) }}
                </button>
                <button
                  v-if="paymentsDone(read)"
                  class="run-list__lnk"
                  type="button"
                  :disabled="busy === `refunds-${read.payments.scrapeJobId}`"
                  @click="downloadPayments(read.payments)"
                >
                  {{ $t('carriers.import.payments.DownloadAllPayments') }}
                </button>
              </p>
            </template>
          </div>

          <div v-if="canRunImport" class="run-list__actions">
            <template v-for="run in read.runs">
              <BaseButton
                v-if="isJobActive(run) && !isStopping(run) && !runsInBackend(run)"
                :key="`stop-${run.scrapeJobId}`"
                is-small
                type="outline"
                :loading="busy === run.scrapeJobId"
                @click="stop(run)"
              >
                {{ $t('carriers.import.Stop') }}<template v-if="read.runs.length > 1"> · {{ $t(`carriers.import.kind.${kindOf(run)}`) }}</template>
              </BaseButton>
              <!-- A read stopped by hand carries on from where it stopped. A failed one has its
                   own button beside the reason. -->
              <BaseButton
                v-else-if="!failureOf(read) && stoppedEarly(run) && canResume(run)"
                :key="`go-${run.scrapeJobId}`"
                is-small
                :loading="busy === run.scrapeJobId"
                @click="proceed(run)"
              >
                {{ $t('carriers.import.Proceed') }}<template v-if="read.runs.length > 1"> · {{ $t(`carriers.import.kind.${kindOf(run)}`) }}</template>
              </BaseButton>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Every policy the read went through, both halves in one list. Stacks over the drawer. -->
    <ReadPoliciesModal
      v-if="allPoliciesOf"
      :payments="allPoliciesOf.payments"
      :policies="allPoliciesOf.policies"
      :carrier-name="allPoliciesOf.lead.carrierName || $t('carriers.import.TheCarrier')"
      @close="allPoliciesOf = null"
    />

    <UpdatePasswordDialog
      v-if="changingPassword"
      :account="changingPassword"
      @close="changingPassword = null"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import BasePreloader from '@/views/RenovaDashboard/components/ui-kit/Buttons/BasePreloader';
import { UserPermissions } from '@/utils/permissions';
import {
  apiError, canResume, etaText, formatCount, formatWhen, groupReads, isDailyRun, isJobActive, isSnapshotRun, isStopping,
  jobKind, newReadId, paymentsLine, policiesLine, progressFigure, progressTotalKey, readFailure, readPolicyCount,
  runProgress, runVariant, runsInBackend, saveFile, variantTone,
} from '@/utils/carrierImport';
import CarrierLogo from './CarrierLogo';
import ReadPoliciesModal from './ReadPoliciesModal';
import TwoFaCodeCard from './TwoFaCodeCard';
import UpdatePasswordDialog from './UpdatePasswordDialog';

/**
 * The runs of one portal account, or of the whole tenant — the same rows either
 * way, so an account's own history and the All updates drawer cannot drift apart
 * in how they describe a run.
 *
 * A row is a record, not a workplace: what the read counted, the policies behind
 * the counts and the payments as a file. Decisions live in Needs review. A read
 * that failed says why in one sentence and offers the one fix.
 */
export default {
  name: 'ImportRunList',
  components: {
    BaseButton,
    BasePreloader,
    CarrierLogo,
    ReadPoliciesModal,
    TwoFaCodeCard,
    UpdatePasswordDialog,
  },
  mixins: [UserPermissions],
  props: {
    runs: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    /** Name the carrier and the login on each row — All updates does, one account's history does not. */
    showWho: { type: Boolean, default: false },
    /** `run -> logo URL or null` for the carrier on each row when `showWho`; null shows the placeholder. */
    logoFor: { type: Function, default: null },
    /** Overrides the empty line — All updates says so when its carrier filter matches nothing. */
    emptyText: { type: String, default: null },
    /**
     * The run to mark as the one just returned from, as `{ scrapeJobId }`.
     *
     * A list you come back to should say where you were; without it a drawer that reopens on the
     * same scroll position still leaves you re-finding the row you had just finished.
     */
    highlight: { type: Object, default: null },
  },
  data() {
    return {
      busy: null,
      // The read whose every policy is listed in ReadPoliciesModal.
      allPoliciesOf: null,
      // The portal account whose password is being replaced in UpdatePasswordDialog.
      changingPassword: null,
      // The read whose sign-in code field is open under its reason.
      enteringCode: null,
    };
  },
  computed: {
    ...mapGetters(['carrierImportAccounts']),
    /** Stopping, resuming and starting a run are policy writes. */
    canRunImport() {
      return this.checkUserPermissions([this.PermissionsTypes.POLICIES_UPDATE]);
    },
    /** The rows: the runs as reads, the two jobs of a "Read both" as one (groupReads). */
    reads() {
      return groupReads(this.runs);
    },
  },
  methods: {
    isDailyRun,
    isJobActive,
    isStopping,
    canResume,
    runsInBackend,
    formatWhen,
    formatCount,
    progressOf: runProgress,
    progressFigure,
    progressTotalKey,
    readPolicyCount,
    kindOf: jobKind,
    failureOf: readFailure,
    /** The run just returned from, so coming back shows where you were. */
    isCurrent(run) {
      return Boolean(this.highlight && this.highlight.scrapeJobId === run.scrapeJobId);
    },
    /** A failed read is painted as one, whichever half failed; otherwise by its lead job. */
    readTone(read) {
      if (readFailure(read)) return 'warning';
      return variantTone(runVariant(read.lead));
    },
    /**
     * The halves to show counts for: all of them, or, when the read failed, the ones that did
     * not. A failed half is said once in the failure line above, not repeated with zeros.
     */
    okRuns(read) {
      const failure = readFailure(read);
      if (!failure) return read.runs;
      return read.runs.filter((run) => !failure.runs.includes(run));
    },
    /**
     * The reason a read failed, in one sentence. The two authenticator cases add what to do, as
     * no button can do it for them: set the app up on the portal, or start a read and type the code.
     */
    failureLine(read) {
      const { variant } = readFailure(read);
      const carrier = read.lead.carrierName || this.$t('carriers.import.TheCarrier');
      const why = this.$t(`carriers.import.variant.${variant}`, { carrier });
      if (variant !== 'twoFaRequired' && variant !== 'twoFaSetupRequired') return why;
      return `${why}. ${this.$t(`carriers.import.fix.${variant}`, { carrier })}`;
    },
    /** "Payments", "Policies", or both for a read of two. */
    kindLabel(read) {
      return read.runs.length > 1
        ? this.$t('carriers.import.kind.BOTH')
        : this.$t(`carriers.import.kind.${jobKind(read.lead)}`);
    },
    /** The non-zero counts of one half, payments or policies. */
    countsOf(run) {
      if (jobKind(run) === 'PAYMENTS') return paymentsLine(run) || [];
      return policiesLine(run);
    },
    accountOf(run) {
      return (this.carrierImportAccounts || []).find((a) => a.scrapeAccountId === run.scrapeAccountId) || null;
    },
    /** The account to type a sign-in code for: the one the waiting run signs in with. */
    codeAccountOf(read) {
      const failure = readFailure(read);
      return failure && failure.runs[0] ? this.accountOf(failure.runs[0]) : null;
    },
    /**
     * The one button beside a failure: Update password, Enter code or Read again. None without
     * the right to start reads, for a login that is gone or turned off, or for a lock the
     * carrier lifts by itself.
     */
    failureAction(read) {
      const failure = readFailure(read);
      if (!failure || !failure.action || !this.canRunImport) return null;
      const account = this.accountOf(failure.runs[0]);
      if (!account) return null;
      if (failure.action === 'password') return { kind: 'password', label: 'carriers.import.password.Update' };
      if (failure.action === 'code') return { kind: 'code', label: 'carriers.import.failure.EnterCode' };
      if (!account.active || failure.runs.some(runsInBackend)) return null;
      return { kind: 'again', label: 'carriers.import.failure.ReadAgain' };
    },
    act(read) {
      const action = this.failureAction(read);
      if (!action) return;
      const failure = readFailure(read);
      if (action.kind === 'password') {
        this.changingPassword = this.accountOf(failure.runs[0]);
      } else if (action.kind === 'code') {
        this.enteringCode = this.enteringCode === read.key ? null : read.key;
      } else {
        this.readAgain(read, failure.runs);
      }
    },
    /**
     * Read the failed halves again: where a run can carry on from where it stopped it does,
     * otherwise a new run of the same kind starts -- both under one read id when there are two.
     */
    readAgain(read, runs) {
      this.busy = read.key;
      const fresh = runs.filter((run) => !canResume(run));
      const readId = fresh.length > 1 ? newReadId() : undefined;
      const kindFor = (run) => {
        if (jobKind(run) === 'PAYMENTS') return 'PAYMENTS';
        return isSnapshotRun(run) ? 'POLICIES_SNAPSHOT' : 'POLICIES';
      };
      // In order, payments first, as the Read dialog queues them.
      runs
        .reduce((chain, run) => chain.then(() => (canResume(run)
          ? this.$store.dispatch('carrierImportProceed', run.scrapeJobId)
          : this.$store.dispatch('carrierImportStart', {
            scrapeAccountId: run.scrapeAccountId, kind: kindFor(run), readId,
          }))), Promise.resolve())
        .then(() => {
          this.$notify.success({ message: this.$t('carriers.import.failure.ReadAgainStarted') });
          return this.$store.dispatch('carrierImportFetchRuns');
        })
        .catch((err) => this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) }))
        .finally(() => { this.busy = null; });
    },
    /** No half still running: the read's links are about something finished. */
    settled(read) {
      return !read.runs.some(isJobActive);
    },
    /** Done with, whether or not it changed anything. */
    finished(run) {
      return Boolean(run) && ['completed', 'nothingChanged', 'cancelled'].includes(runVariant(run));
    },
    paymentsDone(read) {
      return this.finished(read.payments);
    },
    /** A staged policies half, or a payments half: something the policy list can show. */
    seeAllShown(read) {
      const listable = this.paymentsDone(read) || (this.finished(read.policies) && isSnapshotRun(read.policies));
      const imported = this.finished(read.policies) && !isSnapshotRun(read.policies) && !read.payments;
      return (listable || imported) && readPolicyCount(read) !== 0;
    },
    /**
     * Every policy of the read. A read that imported straight into the book (started from
     * Integrations) staged nothing to list: its policies are the Policies page, filtered to it.
     */
    seeAll(read) {
      if (!read.payments && read.policies && !isSnapshotRun(read.policies)) {
        this.openInPolicies(read.policies);
        return;
      }
      this.allPoliciesOf = read;
    },
    downloadPayments(run) {
      this.busy = `refunds-${run.scrapeJobId}`;
      this.$store.dispatch('carrierImportExportRefundReceipts', { scrapeJobId: run.scrapeJobId, jobType: run.jobType })
        .then(({ blob, fileName }) => saveFile(blob, fileName))
        .catch((err) => this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) }))
        .finally(() => { this.busy = null; });
    },
    /**
     * What to say while the size of the book is still unknown.
     *
     * True whether the worker has simply not sent its first part yet — which on a
     * portal that emails a sign-in code is minutes — or this frontend is talking
     * to a backend that does not report position at all. Payments has no book to
     * size, so it gets the plain running state instead.
     */
    unknownProgressText(run) {
      return jobKind(run) === 'PAYMENTS'
        ? this.$t('carriers.import.state.running')
        : this.$t('carriers.import.running.SizingUp');
    },
    etaOf(run) {
      return etaText(run, this.$t.bind(this));
    },
    /** A finished run that read part of the book and stopped. */
    stoppedEarly(run) {
      return !isJobActive(run) && runVariant(run) === 'cancelled';
    },
    /** "Including member policies inside group policies: 12 added", only the non-zero parts; '' for none. */
    memberPoliciesLine(run) {
      const parts = [
        [Number(run.memberPoliciesCreated) || 0, 'carriers.import.history.MemberAdded'],
        [Number(run.memberPoliciesUpdated) || 0, 'carriers.import.history.MemberUpdated'],
      ]
        .filter(([count]) => count > 0)
        .map(([count, key]) => this.$t(key, { count: formatCount(count) }));
      return parts.length ? this.$t('carriers.import.history.MemberPoliciesOf', { parts: parts.join(' · ') }) : '';
    },
    /**
     * The Policies page, filtered to this run, in a new tab.
     *
     * A run id rather than the run's policy ids: a whole-book read imports
     * thousands and that list belongs in no query string. The page resolves it
     * against the backend's scrapeJobIds filter.
     */
    openInPolicies(run) {
      const { href } = this.$router.resolve({
        path: '/admin/policies',
        // runAt only names the chip over there; the filter is the id.
        query: { scrapeJobId: run.scrapeJobId, runAt: run.finishedAt || run.updatedAt },
      });
      window.open(href, '_blank', 'noopener');
    },
    stop(run) {
      this.busy = run.scrapeJobId;
      this.$store.dispatch('carrierImportStop', run.scrapeJobId)
        .then(() => this.$notify.success({ message: this.$t('carriers.import.StoppingHint') }))
        .catch((err) => this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) }))
        .finally(() => { this.busy = null; });
    },
    proceed(run) {
      this.busy = run.scrapeJobId;
      this.$store.dispatch('carrierImportProceed', run.scrapeJobId)
        .then(() => this.$notify.success({ message: this.$t('carriers.import.ProceedStarted') }))
        .catch((err) => this.$notify.error({ message: apiError(err, this.$t('adminTitles.someError')) }))
        .finally(() => { this.busy = null; });
    },
  },
};
</script>

<style lang="scss" scoped>
// Success, warning and danger text have no token in the app's palette — these
// three are the design's own, and are the only literals here.
$ink-success: #2e7d32;
$ink-warning: #996c00;

.run-list {
  &__empty {
    padding: 24px 0;
    font-size: 14px;
    color: var(--solid-dark-purple);
  }

  &__run {
    border-bottom: 1px solid var(--solid-lavender-grey);

    &:last-child { border-bottom: 0; }

    // The row just returned from. An inset rule rather than a border: the row already has a
    // bottom border, and a real one on the left would shift every line in by its width the
    // moment you came back.
    &--current > .run-list__head {
      box-shadow: inset 3px 0 0 var(--solid-purple);
      background: var(--solid-lavender-ligth);
    }
  }

  &__head {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    padding: 16px 0;
    cursor: pointer;
  }

  &__badge {
    width: 26px;
    height: 26px;
    border-radius: 7px;
    display: grid;
    place-items: center;
    font-size: 12px;
    font-weight: 700;
    flex: none;
    background: var(--solid-grey-light);
    color: var(--solid-dark-purple);

    &--success { background: rgba(88, 219, 77, 0.16); color: $ink-success; }
    &--warning { background: var(--solid-milk-ornage); color: $ink-warning; }
    &--info { background: var(--solid-light-purple); color: var(--solid-purple); }
  }

  &__main { flex: 1; min-width: 0; }

  &__when {
    font-size: 15px;
    font-weight: 600;
    color: var(--solid-black-purple);
    letter-spacing: -0.015em;
  }

  &__kind,
  &__live {
    font-size: 13px;
    font-weight: 400;
    color: var(--solid-dark-purple);
  }

  &__daily {
    display: inline-block;
    margin-left: 6px;
    padding: 0 7px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 500;
    background: #eeebfd;
    color: #6f5ce6;
    vertical-align: 1px;
  }

  &__partial {
    font-size: 13px;
    font-weight: 500;
    color: var(--solid-orange);
  }

  &__chevron {
    color: var(--solid-dark-purple);
    font-size: 11px;
    margin-left: 8px;
  }

  // The carrier leads the row's second line, mark and name, so a list of several reads by eye.
  &__who {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 6px;
    min-width: 0;
    font-size: 13px;
  }

  &__carrier {
    font-weight: 600;
    color: var(--solid-black-purple);
    white-space: nowrap;
  }

  &__login {
    color: var(--solid-dark-purple);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // The second half of a read of two sits under the first, ruled off, each under its name.
  &__half--paired {
    margin-top: 8px;
    padding-left: 10px;
    border-left: 2px solid var(--solid-lavender-grey);
  }

  &__half-kicker {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--solid-dark-purple);
    margin-bottom: 2px;
  }

  &__sum {
    font-size: 13.5px;
    line-height: 1.55;
    color: var(--solid-black-purple-70);
    margin-top: 7px;
    font-variant-numeric: tabular-nums;

    b {
      font-weight: 600;
      color: var(--solid-black-purple);
    }
  }

  &__muted { color: var(--solid-dark-purple); }

  &__ml-sub {
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    margin-top: 3px;
  }

  // The reason a read failed and its one button, on one line.
  &__failure {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 12px;
    margin-top: 7px;
  }

  &__failure-text {
    font-size: 13.5px;
    line-height: 1.55;
    color: var(--solid-black-purple-70);
  }

  &__code { margin-top: 10px; }

  &__links {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 18px;
    margin-top: 8px;
  }

  &__lnk {
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
    color: var(--solid-purple);
    font-weight: 600;
    font-size: 13px;
    white-space: nowrap;

    &:disabled { opacity: 0.55; cursor: default; }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: none;
  }
}
</style>
